import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PackageIcon, CheckCircleIcon, ClockIcon, TruckIcon, UtensilsIcon, XCircleIcon, ArrowLeftIcon, StoreIcon, RefreshCwIcon } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { CONFIG } from '../data/config';
import { Order, OrderStatus, STATUS_CONFIG } from '../admin/types';
interface OrderTrackingProps {
  token: string;
}
const STATUS_STEPS: OrderStatus[] = ['new', 'accepted', 'preparing', 'ready', 'out_for_delivery', 'delivered'];
const STEP_LABELS: Record<string, string> = {
  new: 'Recibido',
  accepted: 'Aceptado',
  preparing: 'Preparando',
  ready: 'Listo',
  out_for_delivery: 'En camino',
  delivered: 'Entregado'
};
export function OrderTracking({
  token
}: OrderTrackingProps) {
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const fetchOrder = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true);
    try {
      const {
        data,
        error: fetchError
      } = await supabase.from('orders').select(`
          order_number,
          status,
          delivery_type,
          created_at,
          total_final
        `).eq('tracking_token', token).single();
      if (fetchError) throw fetchError;
      if (data) {
        setOrder(data as Order);
        setLastUpdated(new Date());
      }
    } catch (err) {
      console.error('Error fetching order:', err);
      if (!order) setError('Pedido no encontrado');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, order]);
  const handleManualRefresh = () => {
    fetchOrder(true);
  };
  useEffect(() => {
    fetchOrder();
    // Realtime subscription
    const channel = supabase.channel('order-tracking').on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'orders'
    }, payload => {
      if (payload.new.tracking_token === token) {
        // Only use public fields from the realtime payload
        setOrder({
          order_number: payload.new.order_number,
          status: payload.new.status,
          delivery_type: payload.new.delivery_type,
          created_at: payload.new.created_at,
          total_final: payload.new.total_final
        } as Order);
        setLastUpdated(new Date());
      }
    }).subscribe();
    // Polling every 15s for faster updates
    const interval = setInterval(() => fetchOrder(), 15000);
    return () => {
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, [token]);
  if (loading) {
    return <div className="min-h-screen bg-carbon flex flex-col items-center justify-center p-4" data-id="element-1106">
        <div className="w-8 h-8 border-4 border-dorado/30 border-t-dorado rounded-full animate-spin mb-4" data-id="element-1107" />
        <p className="text-gray-400 font-medium" data-id="element-1108">Buscando tu pedido...</p>
      </div>;
  }
  if (error || !order) {
    return <div className="min-h-screen bg-carbon flex flex-col items-center justify-center p-4 text-center" data-id="element-1109">
        <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mb-4 border border-red-500/20" data-id="element-1110">
          <XCircleIcon className="w-8 h-8 text-red-500" data-id="element-1111" />
        </div>
        <h1 className="font-heading text-2xl text-white mb-2" data-id="element-1112">
          Pedido no encontrado
        </h1>
        <p className="text-gray-400 mb-8 max-w-xs" data-id="element-1113">
          No pudimos encontrar un pedido con este código de seguimiento.
        </p>
        <button onClick={() => window.location.href = '/'} className="px-6 py-3 bg-surface border border-gray-700 rounded-xl text-white font-medium hover:bg-surface-light transition-colors flex items-center gap-2" data-id="element-1114">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-1115" />
          Volver al menú
        </button>
      </div>;
  }
  const currentStepIndex = STATUS_STEPS.indexOf(order.status);
  const isCancelled = order.status === 'cancelled';
  return <div className="min-h-screen bg-carbon w-full flex justify-center" data-id="element-1116">
      <div className="w-full max-w-md bg-carbon min-h-screen relative shadow-2xl overflow-x-hidden flex flex-col" data-id="element-1117">
        {/* Header */}
        <header className="bg-surface border-b border-gray-800 sticky top-0 z-30 px-4 py-3" data-id="element-1118">
          <div className="flex items-center justify-between" data-id="element-1119">
            <div className="flex items-center gap-3" data-id="element-1120">
              <div className="w-10 h-10 bg-dorado/10 rounded-xl flex items-center justify-center border border-dorado/20" data-id="element-1121">
                <PackageIcon className="w-5 h-5 text-dorado" data-id="element-1122" />
              </div>
              <div data-id="element-1123">
                <h1 className="font-heading text-lg text-white leading-tight" data-id="element-1124">
                  {CONFIG.APP_NAME}
                </h1>
                <p className="text-xs text-gray-400" data-id="element-1125">Seguimiento de pedido</p>
              </div>
            </div>
            <div className="flex items-center gap-2" data-id="element-1126">
              <button onClick={handleManualRefresh} disabled={refreshing} className="p-2 bg-surface-light border border-gray-700 rounded-lg text-gray-400 hover:text-dorado hover:border-dorado/30 transition-colors active:scale-95" data-id="element-1127">
                <RefreshCwIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} data-id="element-1128" />
              </button>
              <button onClick={() => window.location.href = '/'} className="text-xs font-medium text-gray-400 hover:text-white transition-colors flex items-center gap-1 bg-surface-light px-3 py-1.5 rounded-lg border border-gray-700" data-id="element-1129">
                Ver menú
              </button>
            </div>
          </div>
          {lastUpdated && <p className="text-[10px] text-gray-600 text-right mt-1" data-id="element-1130">
              Actualizado:{' '}
              {lastUpdated.toLocaleTimeString('es-CR', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          })}
            </p>}
        </header>

        <main className="flex-1 p-4 space-y-6" data-id="element-1131">
          {/* Order Summary Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="bg-surface border border-gray-800 rounded-2xl p-6 shadow-lg" data-id="element-1132">
            <div className="flex justify-between items-start mb-6" data-id="element-1133">
              <div data-id="element-1134">
                <p className="text-sm text-gray-400 mb-1" data-id="element-1135">Orden</p>
                <h2 className="font-heading text-3xl text-white tracking-wider" data-id="element-1136">
                  #{order.order_number}
                </h2>
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-bold border ${STATUS_CONFIG[order.status].bg} ${STATUS_CONFIG[order.status].color}`} data-id="element-1137">
                {STATUS_CONFIG[order.status].label}
              </div>
            </div>

            <div className="space-y-3 text-sm" data-id="element-1138">
              <div className="flex justify-between border-b border-gray-800 pb-3" data-id="element-1139">
                <span className="text-gray-400" data-id="element-1140">Entrega</span>
                <span className="text-white font-medium flex items-center gap-1.5" data-id="element-1141">
                  {order.delivery_type === 'express' ? <>
                      Express <TruckIcon className="w-3.5 h-3.5 text-dorado" data-id="element-1142" />
                    </> : <>
                      Retiro <StoreIcon className="w-3.5 h-3.5 text-dorado" data-id="element-1143" />
                    </>}
                </span>
              </div>
              <div className="flex justify-between pt-1" data-id="element-1144">
                <span className="text-gray-400" data-id="element-1145">Total</span>
                <span className="text-white font-bold text-base" data-id="element-1146">
                  {CONFIG.CURRENCY_SYMBOL}
                  {order.total_final.toLocaleString()}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Progress Bar */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="bg-surface border border-gray-800 rounded-2xl p-6 shadow-lg" data-id="element-1147">
            <h3 className="font-heading text-lg text-white mb-6" data-id="element-1148">
              Progreso del pedido
            </h3>

            {isCancelled ? <div className="flex flex-col items-center text-center py-6" data-id="element-1149">
                <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mb-4 border border-red-500/20" data-id="element-1150">
                  <XCircleIcon className="w-8 h-8 text-red-500" data-id="element-1151" />
                </div>
                <h4 className="text-red-400 font-bold text-lg mb-2" data-id="element-1152">
                  Pedido Cancelado
                </h4>
                <p className="text-gray-400 text-sm" data-id="element-1153">
                  Este pedido ha sido cancelado. Si tienes dudas, contáctanos al
                  WhatsApp.
                </p>
              </div> : <div data-id="element-1154">
                {/* Horizontal bar with dots */}
                <div className="relative flex items-center justify-between mb-3" data-id="element-1155">
                  {/* Background line */}
                  <div className="absolute left-3 right-3 top-1/2 -translate-y-1/2 h-1 bg-gray-800 rounded-full" data-id="element-1156" />

                  {/* Filled line */}
                  <motion.div className="absolute left-3 top-1/2 -translate-y-1/2 h-1 bg-dorado rounded-full" initial={{
                width: '0%'
              }} animate={{
                width: `${Math.max(0, currentStepIndex / (STATUS_STEPS.length - 1) * 100)}%`
              }} transition={{
                duration: 0.8,
                ease: 'easeOut'
              }} style={{
                maxWidth: 'calc(100% - 24px)'
              }} data-id="element-1157" />

                  {/* Dots */}
                  {STATUS_STEPS.map((step, index) => {
                const isCompleted = currentStepIndex > index;
                const isCurrent = currentStepIndex === index;
                return <div key={step} className="relative z-10 flex flex-col items-center" data-id="element-1158">
                        <motion.div initial={{
                    scale: 0.8
                  }} animate={{
                    scale: isCurrent ? 1.2 : 1
                  }} className={`w-6 h-6 rounded-full flex items-center justify-center border-2 transition-colors duration-500 ${isCompleted ? 'bg-dorado border-dorado' : isCurrent ? 'bg-dorado border-dorado ring-4 ring-dorado/20' : 'bg-gray-800 border-gray-700'}`} data-id="element-1159">
                          {isCompleted ? <CheckCircleIcon className="w-3.5 h-3.5 text-carbon" data-id="element-1160" /> : isCurrent ? <div className="w-2 h-2 bg-carbon rounded-full animate-pulse" data-id="element-1161" /> : <div className="w-1.5 h-1.5 bg-gray-600 rounded-full" data-id="element-1162" />}
                        </motion.div>
                      </div>;
              })}
                </div>

                {/* Labels below dots */}
                <div className="flex justify-between" data-id="element-1163">
                  {STATUS_STEPS.map((step, index) => {
                const isCompleted = currentStepIndex > index;
                const isCurrent = currentStepIndex === index;
                return <div key={step} className="flex flex-col items-center" style={{
                  width: `${100 / STATUS_STEPS.length}%`
                }} data-id="element-1164">
                        <span className={`text-[10px] leading-tight text-center font-medium mt-2 ${isCurrent ? 'text-dorado' : isCompleted ? 'text-white' : 'text-gray-600'}`} data-id="element-1165">
                          {STEP_LABELS[step]}
                        </span>
                      </div>;
              })}
                </div>

                {/* Current status message */}
                <div className="mt-6 bg-surface-light border border-gray-700 rounded-xl p-4 flex items-center gap-3" data-id="element-1166">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${STATUS_CONFIG[order.status].bg}`} data-id="element-1167">
                    <div className={`w-3 h-3 rounded-full ${STATUS_CONFIG[order.status].dot} animate-pulse`} data-id="element-1168" />
                  </div>
                  <div data-id="element-1169">
                    <p className={`text-sm font-bold ${STATUS_CONFIG[order.status].color}`} data-id="element-1170">
                      {STATUS_CONFIG[order.status].label}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1171">
                      {order.status === 'new' && 'Recibimos tu orden'}
                      {order.status === 'accepted' && 'Orden confirmada por cocina'}
                      {order.status === 'preparing' && 'Cocinando tu comida 🔥'}
                      {order.status === 'ready' && (order.delivery_type === 'express' ? 'Esperando al repartidor' : '¡Listo para retirar!')}
                      {order.status === 'out_for_delivery' && 'Tu pedido está en camino 🛵'}
                      {order.status === 'delivered' && '¡Que lo disfrutes! 🎉'}
                    </p>
                  </div>
                </div>
              </div>}
          </motion.div>

          {/* Footer Info */}
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.2
        }} className="text-center text-xs text-gray-500 pt-4" data-id="element-1172">
            Se actualiza automáticamente · Tocá 🔄 para actualizar ahora
          </motion.p>
        </main>
      </div>
    </div>;
}